import { Link, SelectField } from '@flos/react-ui';
import CoverableDetailsTemplate from '../../../../templates/CoverableDetailsTemplate';
import WizardPagetemplate from '../../../../templates/WizardPagetemplate';
import { useRecoilValue } from 'recoil';
import { allCoverablesOnsaAtom } from '../../../../recoil/atoms/allCoverablesOnsaAtom';
import { getOnsaCurrentProductSelector } from '../../../../recoil/selectors/getOnsaCurrentProductSelector';
import { useState } from 'react';
import { getCoverableIndex } from '../../../../utils/coverablesUtil';
import { useHippoText } from '../../../../hooks/useHippoText';
import { useGetVMRData } from '../../../../hooks/useGetVMRData';
import type { OnsaMvpVmrPopulatorDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa/motor';
import type { MACPropellant_Top } from 'gw-edge-client/dist/types/dto/pc/typekey';
import { carMakeSelector } from '../../../../recoil/selectors/carMakeSelector';
import { useUpdateCoverableInformation } from '../../../../recoil/hooks/useUpdateCoverableInformation';
import { track } from '../../../../utils/trackingUtil';
import { useSaveVMRData } from '../../../../hooks/useSaveVMRData';
import { useSetVehicleInheritanceFields } from '../../../../hooks/useSetVehicleInheritanceFields';

const NoRegistrationNumberStep = ({
    goNext,
    goPrevious
}: {
    goNext: () => void;
    goPrevious: () => void;
}): React.JSX.Element => {
    const coverables = useRecoilValue(allCoverablesOnsaAtom);
    const currentProduct = useRecoilValue(getOnsaCurrentProductSelector);
    const getHippoText = useHippoText();
    const { cars = [] } = coverables;
    const coverableIndex = getCoverableIndex(cars, currentProduct.fixedId);
    const { setVehicleInheritanceFields } = useSetVehicleInheritanceFields();
    const coverable = cars[coverableIndex];
    const { getVMRData } = useGetVMRData();
    const { saveVMRData } = useSaveVMRData();
    const initialCarVMRData = useRecoilValue(carMakeSelector);
    const { updateCoverableInformation } = useUpdateCoverableInformation();
    const [VMRData, setVMRData] = useState<OnsaMvpVmrPopulatorDTO>({
        ...initialCarVMRData
    });
    const doesVMRDataExist: boolean =
        VMRData.make !== undefined &&
        VMRData.fuelType !== undefined &&
        VMRData.model !== undefined &&
        VMRData.year !== undefined &&
        VMRData.onsaEngineSize !== undefined &&
        VMRData.onsaEnginePower !== undefined;

    const handleVMRUpdate = async (newVMRData: OnsaMvpVmrPopulatorDTO): Promise<void> => {
        await getVMRData(newVMRData).then((data) => {
            setVMRData({ ...data, vehicleID: coverable.fixedId });
        });
    };

    const goToNextPage = (): void => {
        track(`${currentProduct.flavourName}ContentCoverableInformationForNoRegistrationNumber`, [
            { make: VMRData.make },
            { fuelType: VMRData.fuelType },
            { model: VMRData.model },
            { year: VMRData.year },
            { engineSize: VMRData.onsaEngineSize },
            { enginePower: VMRData.onsaEnginePower }
        ]);
        void saveVMRData(VMRData).then(async () => {
            updateCoverableInformation({
                make: VMRData.make,
                model: VMRData.model,
                variant: VMRData.variant
            });
            await setVehicleInheritanceFields();
            goNext();
        });
    };

    return (
        <WizardPagetemplate
            onNext={goToNextPage}
            onPrevious={goPrevious}
            hidePrevious
            disableNext={!doesVMRDataExist}
        >
            <CoverableDetailsTemplate
                header={getHippoText('DSS-COVERABLE-INFORMATION-STEP-VMR-HEADER')}
            >
                <SelectField
                    label={getHippoText('DSS-COVERABLE-INFORMATION-STEP-VMR-MAKE')}
                    options={VMRData.makeDisplayList}
                    onChange={(e) => {
                        const makeData = {
                            ...VMRData,
                            make: e.target.value,
                            lookUpFieldName: 'FuelType'
                        };
                        void handleVMRUpdate(makeData);
                    }}
                />
                {VMRData.make && VMRData.fuelTypeDisplayList ? (
                    <SelectField
                        value={VMRData.fuelType ?? ''}
                        label={getHippoText('DSS-COVERABLE-INFORMATION-STEP-VMR-FUEL-TYPE')}
                        options={VMRData.fuelTypeDisplayList.map((fueltype) => {
                            return {
                                label: fueltype.displayName,
                                value: fueltype.code
                            };
                        })}
                        onChange={(fuelTypeCode) => {
                            const fuelData = {
                                ...VMRData,
                                fuelType: fuelTypeCode.target.value as MACPropellant_Top,
                                lookUpFieldName: 'Model'
                            };
                            void handleVMRUpdate(fuelData);
                        }}
                    />
                ) : (
                    <></>
                )}
                {VMRData.fuelType ? (
                    <SelectField
                        value={VMRData.model ?? ''}
                        label={getHippoText('DSS-COVERABLE-INFORMATION-STEP-VMR-MODEL')}
                        options={VMRData.modelDisplayList}
                        onChange={(e) => {
                            const modelData = {
                                ...VMRData,
                                model: e.target.value,
                                lookUpFieldName: 'Year'
                            };
                            void handleVMRUpdate(modelData);
                        }}
                    />
                ) : (
                    <></>
                )}
                {VMRData.model ? (
                    <SelectField
                        value={VMRData.year ?? ''}
                        label={getHippoText('DSS-COVERABLE-INFORMATION-STEP-VMR-YEAR')}
                        options={VMRData.vehicleYearDisplayList}
                        onChange={(e) => {
                            const yearData = {
                                ...VMRData,
                                year: Number(e.target.value),
                                lookUpFieldName: 'Engine'
                            };
                            void handleVMRUpdate(yearData);
                        }}
                    />
                ) : (
                    <></>
                )}
                {VMRData.year ? (
                    <SelectField
                        value={VMRData.onsaEngineSize ?? ''}
                        label={getHippoText('DSS-COVERABLE-INFORMATION-STEP-VMR-ENGINE-SIZE')}
                        options={VMRData.engineSizeDisplayList}
                        onChange={(e) => {
                            const yearData = {
                                ...VMRData,
                                onsaEngineSize: e.target.value,
                                lookUpFieldName: 'Engine_size'
                            };
                            void handleVMRUpdate(yearData);
                        }}
                    />
                ) : (
                    <></>
                )}
                {VMRData.onsaEngineSize ? (
                    <SelectField
                        label={getHippoText('DSS-COVERABLE-INFORMATION-STEP-VMR-EFFECT')}
                        options={VMRData.enginePowerDisplayList}
                        onChange={(e) => {
                            const yearData = {
                                ...VMRData,
                                onsaEnginePower: Number(e.target.value.split('k')[0]),
                                lookUpFieldName: 'Variant'
                            };
                            void handleVMRUpdate(yearData);
                        }}
                    />
                ) : (
                    <></>
                )}
                <div className="u-text-right">
                    <Link theme={'logo'} onClick={goPrevious}>
                        {getHippoText(
                            'DSS-COVERABLE-INFORMATION-STEP-VMR-ENTER-REGISTRATION-PLATE'
                        )}
                    </Link>
                </div>
            </CoverableDetailsTemplate>
        </WizardPagetemplate>
    );
};

export default NoRegistrationNumberStep;
